import { resolveConfidenceVisitorId } from "@helpers/confidenceVisitorId"
import * as Sentry from "@sentry/react"
import { setUserId } from "@snowplow/browser-tracker"
import {
  AuthError,
  createClient,
  FunctionsHttpError,
  Session,
  User,
} from "@supabase/supabase-js"
import {
  CancelRevenueCatSubscriptionsData,
  EdgeFunctionError,
  EventName,
  RefundRevenueCatSubscriptionsData,
  StripePrice,
} from "src/types"
import i18n from "../i18n"
import { getIsFemaleFromLocalStorage } from "./localStorage"
import { getUserCountryCode, getUserId } from "./sessionStorage"

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

export const supabase = createClient(supabaseUrl, supabaseKey)

export async function cancelRevenueCatSubscriptions(): Promise<CancelRevenueCatSubscriptionsData> {
  const response = await supabase.functions.invoke(
    "cancel-revenuecat-subscriptions",
  )

  const data = response.data as CancelRevenueCatSubscriptionsData
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function createStripeSubscription({
  currency,
  email,
  priceId,
}: {
  currency: string
  email: string | undefined
  priceId: string
}) {
  const response = await supabase.functions.invoke(
    "create-stripe-subscription",
    {
      body: {
        currency,
        email,
        priceId,
      },
    },
  )

  const data = response.data as {
    clientSecret: string
    subscriptionId: string
  }

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return { data }
}

export async function createStripeInvoice({
  currency,
  email,
  priceId,
}: {
  currency: string
  email: string | undefined
  priceId: string
}) {
  const response = await supabase.functions.invoke("create-stripe-invoice", {
    body: {
      currency,
      email,
      priceId,
    },
  })

  const data = response.data as {
    clientSecret: string
    invoiceCurrency: string
    paymentIntentId: string
  }

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return { data }
}

export async function getUser(): Promise<{
  data: { user: User | null }
  error: AuthError | null
}> {
  const { data, error } = await supabase.auth.getUser()

  return { data, error }
}

export async function getSession(): Promise<{
  data: { session: Session | null }
  error: AuthError | null
}> {
  const { data, error } = await supabase.auth.getSession()

  return { data, error }
}

export async function listStripePrices(): Promise<StripePrice[]> {
  const response = await supabase.functions.invoke("list-stripe-prices", {
    body: {
      productId: import.meta.env.VITE_STRIPE_PRODUCT_ID as string,
    },
  })

  const data = response.data as StripePrice[]
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function refundRevenueCatSubscriptions(): Promise<RefundRevenueCatSubscriptionsData> {
  const response = await supabase.functions.invoke(
    "refund-revenuecat-subscriptions",
  )

  const data = response.data as RefundRevenueCatSubscriptionsData
  const error = response.error as EdgeFunctionError

  if (error) throw error

  return data
}

export async function resetPasswordForEmail({ email }: { email: string }) {
  const redirectTo = `${window.location.origin}/account/reset-password`

  const response = await supabase.functions.invoke(
    "send-password-recovery-email",
    { body: { email, redirectTo } },
  )

  if (response.error instanceof FunctionsHttpError) {
    const status = response.error.context?.status
    if (status === 404) {
      return { isEmailNotFound: true, isRateLimited: false, error: null }
    }
    if (status === 429) {
      return { isEmailNotFound: false, isRateLimited: true, error: null }
    }
    Sentry.captureException(response.error)
    return {
      isEmailNotFound: false,
      isRateLimited: false,
      error: response.error,
    }
  }

  if (response.error) {
    Sentry.captureException(response.error)
    return {
      isEmailNotFound: false,
      isRateLimited: false,
      error: response.error,
    }
  }

  return { isEmailNotFound: false, isRateLimited: false, error: null }
}

export async function trackEvent({
  currency,
  eventId,
  eventName,
  eventSourceUrl,
  fbc,
  fbcFromCookie,
  fbcFromLocalStorage,
  fbp,
  pixelId,
  userCity,
  userCountryCode,
  userEmail,
  userId,
  userZipCode,
  value,
  planPrice,
}: {
  currency?: string
  eventId: string
  eventName: EventName
  eventSourceUrl: string
  fbc: string | undefined | null
  fbcFromCookie: string | undefined | null
  fbcFromLocalStorage: string | undefined | null
  fbp: string | undefined
  pixelId: string
  userCity: string | undefined
  userCountryCode: string | undefined
  userEmail: string | undefined
  userId: string | undefined
  userZipCode: string | undefined
  value?: number
  planPrice?: number
}) {
  const response = await supabase.functions.invoke("track-event", {
    body: {
      currency,
      eventId,
      eventName,
      eventSourceUrl,
      fbc,
      fbcFromCookie,
      fbcFromLocalStorage,
      fbp,
      pixelId,
      userCity,
      userCountryCode,
      userEmail,
      userId,
      userZipCode,
      value,
      planPrice,
      url: window.location.href,
    },
  })

  const error = response.error as EdgeFunctionError

  if (error) Sentry.captureException(error)
}

export async function signInWithPassword({
  email,
  password,
}: {
  email: string
  password: string
}) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  })

  if (error) Sentry.captureException(error)

  if (data.user?.id) {
    setUserId(data.user.id)
  }

  return { data, error }
}

export async function signUp({
  email,
  password,
}: {
  email: string
  password: string
}) {
  const confidenceVisitorId = resolveConfidenceVisitorId()

  if (!confidenceVisitorId) {
    Sentry.captureMessage("No confidence visitor id found")
  }

  // Written straight into the signup metadata instead of through
  // mergeWebConfidenceVisitorIdentity: signup creates the account, so there is
  // no concurrent writer to merge with, and with email confirmation enabled
  // there may be no session yet for the RPC to authenticate against.
  //
  // The resolver returns undefined where an id cannot exist at all, rather than
  // the "unknown" placeholder it used to return, so there is nothing to filter
  // out here beyond the absence itself.
  const confidenceVisitorIdentity = confidenceVisitorId
    ? {
        confidence_visitor_ids: [confidenceVisitorId],
        original_confidence_visitor_id: confidenceVisitorId,
      }
    : {}

  const options = {
    data: {
      ...confidenceVisitorIdentity,
      country: await getUserCountryCode(),
      language: i18n.language,
      sign_up_platform: "web",
    },
  }

  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: options,
  })

  if (error) Sentry.captureException(error)

  return { error }
}

export async function updatePassword({ password }: { password: string }) {
  const { error } = await supabase.auth.updateUser({
    password,
  })

  if (error) Sentry.captureException(error)

  return { error }
}

export async function storeGender() {
  const userId = await getUserId()

  const { error } = await supabase.from("profiles").insert({
    is_female: getIsFemaleFromLocalStorage(),
    user_id: userId,
  })

  if (error) Sentry.captureException(error)
}

export async function setRevenueCatCustomerEmail() {
  const response = await supabase.functions.invoke(
    "set-revenuecat-customer-email",
  )

  const error = response.error as EdgeFunctionError

  if (error) console.error(error)
}

export async function grantRevenueCatEntitlement({
  paymentIntentId,
}: {
  paymentIntentId: string
}) {
  const response = await supabase.functions.invoke(
    "grant-revenuecat-entitlement",
    {
      body: { paymentIntentId },
    },
  )

  const error = response.error as EdgeFunctionError

  if (error) throw error

  return response.data
}
